// Display-only anchors: stored text and existing author-supplied IDs stay intact.
export function prepareArticle(html = "") {
  const document = new DOMParser().parseFromString(html || "", "text/html");
  // Legacy empty paragraphs became NBSP-only block-level text during migration.
  // Keep their exact text, but prevent anonymous blank lines in the reading view.
  const walker = document.createTreeWalker(document.body, 4);
  const spacers = [];
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (!node.textContent.trim() && node.textContent.includes("\u00a0") &&
        node.parentElement.matches("body, section.article-section")) spacers.push(node);
  }
  spacers.forEach((node) => {
    const spacer = document.createElement("span");
    spacer.hidden = true;
    node.replaceWith(spacer);
    spacer.append(node);
  });
  const used = new Set(Array.from(document.querySelectorAll("[id]"), (el) => el.id));
  const headings = Array.from(document.body.querySelectorAll("h2,h3,h4,h5,h6"))
    .filter((el) => el.textContent.trim() && !el.hidden)
    .map((el, index) => {
      if (!el.id) {
        let id = `article-section-${index + 1}`;
        while (used.has(id)) id += "-section";
        el.id = id;
        used.add(id);
      }
      return { id: el.id, text: el.textContent.trim(), level: Number(el.tagName[1]) };
    });
  return { html: document.body.innerHTML, headings };
}

// A second, reviewed local pass: real sections and lists, not visual-only styling.
export function refineArticleHtml(html, subsectionIndices = [], title = "") {
  const document = new DOMParser().parseFromString(html, "text/html");
  const body = document.body;
  const unwrap = (element) => element.replaceWith(...element.childNodes);
  // Existing sections make the transformation safe to repeat after editing.
  body.querySelectorAll("section.article-section").forEach(unwrap);
  body.querySelectorAll("h2,h3").forEach((heading, index) => {
    const tag = subsectionIndices.includes(index) ? "h3" : "h2";
    if (heading.localName !== tag) {
      const replacement = document.createElement(tag);
      Array.from(heading.attributes).forEach(({ name, value }) => replacement.setAttribute(name, value));
      replacement.append(...heading.childNodes);
      heading.replaceWith(replacement);
    }
  });
  body.querySelectorAll("span:not([style]):not([id]):not([lang]):not([dir])").forEach(unwrap);
  body.querySelectorAll("p").forEach((paragraph) => {
    const strong = paragraph.querySelector("strong,b");
    if (strong && paragraph.textContent.length > 140 && paragraph.textContent.trim() === strong.textContent.trim()) {
      unwrap(strong);
    }
    if (!paragraph.textContent.trim() && !paragraph.querySelector("img,video,audio,iframe,input")) {
      // Keep every original whitespace character, but remove empty layout blocks.
      paragraph.replaceWith(document.createTextNode(paragraph.textContent));
    }
  });
  body.querySelectorAll("hr").forEach((rule) => rule.remove());
  let list = null;
  Array.from(body.childNodes).forEach((node) => {
    if (node.nodeType === 3 && !node.textContent.trim()) {
      if (list) list.append(node);
      return;
    }
    if (node.nodeType === 1 && node.localName === "p" && /^\s*(?:[-–]\s+|[0-9۰-۹]+[ـ.)-]\s*)/.test(node.textContent)) {
      if (!list) {
        list = document.createElement("ul");
        list.className = "article-authored-list";
        node.before(list);
      }
      const item = document.createElement("li");
      Array.from(node.attributes).forEach(({ name, value }) => item.setAttribute(name, value));
      item.append(...node.childNodes);
      list.append(item);
      node.remove();
    } else list = null;
  });
  const anchored = prepareArticle(body.innerHTML);
  body.innerHTML = anchored.html;
  const firstHeading = body.querySelector("h2");
  const normalizedTitle = (text) => text.trim().replace(/\s+/g, " ");
  if (title && firstHeading && normalizedTitle(firstHeading.textContent) === normalizedTitle(title)) {
    // The exact title remains in the HTML and page H1, without a duplicate visual title.
    firstHeading.hidden = true;
  }
  let section = null;
  Array.from(body.childNodes).forEach((node) => {
    if (node.nodeType === 1 && node.localName === "h2") {
      section = document.createElement("section");
      section.className = "article-section";
      section.setAttribute("aria-labelledby", node.id);
      node.before(section);
    }
    if (section) section.append(node);
  });
  return body.innerHTML;
}

// Used only by the reviewed local migration, never to guess headings on page views.
export function structureArticleHtml(html, headingParagraphs = []) {
  const document = new DOMParser().parseFromString(html, "text/html");
  const selected = new Set(headingParagraphs);
  Array.from(document.body.querySelectorAll("p")).forEach((paragraph, index) => {
    if (!selected.has(index)) return;
    const heading = document.createElement("h2");
    Array.from(paragraph.attributes).forEach((attr) => heading.setAttribute(attr.name, attr.value));
    heading.append(...paragraph.childNodes);
    paragraph.replaceWith(heading);
  });
  const existing = Array.from(document.body.querySelectorAll("h1,h2,h3,h4,h5,h6"));
  let previousLevel = 1;
  existing.forEach((heading) => {
    const level = Math.min(Math.max(2, Number(heading.tagName[1])), previousLevel + 1);
    previousLevel = level;
    if (heading.tagName !== `H${level}`) {
      const replacement = document.createElement(`h${level}`);
      Array.from(heading.attributes).forEach((attr) => replacement.setAttribute(attr.name, attr.value));
      replacement.append(...heading.childNodes);
      heading.replaceWith(replacement);
    }
  });
  document.body.querySelectorAll("[style]").forEach((element) => {
    // Strip pasted presentation only; preserve semantic emphasis, lists and media sizing.
    ["color", "background-color", "font-family", "font-size", "line-height", "text-align"]
      .forEach((property) => element.style.removeProperty(property));
    if (!element.getAttribute("style")) element.removeAttribute("style");
  });
  document.body.querySelectorAll("[class]").forEach((element) => {
    const classes = Array.from(element.classList).filter((name) => /^ql-(indent-\d+|direction-rtl|syntax)$/.test(name));
    if (classes.length) element.className = classes.join(" ");
    else element.removeAttribute("class");
  });
  return document.body.innerHTML;
}
